import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React from 'react';
import { ClockIcon, LightBulbIcon, QuestionIcon } from '@primer/octicons-react';
import { truncateMiddle } from '../../format';
import { formatChipDuration } from '../../format';
import { AssertionCheckIcon, AssertionXIcon, CommandIcon } from './CommandIcons';
import { ApiModal } from './ApiModal';
import styles from './steps.module.css';
import { CustomScriptModal } from './CustomScriptModal';
import { SnippetVariablesModal } from './SnippetVariablesModal';

/** kaneai ASSERTION_OPERATOR_DISPLAY (spec §assertRender). */
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
var OPERATOR_DISPLAY = {
  equals: '==',
  not_equals: '!=',
  contains: 'contains',
  not_contains: 'not contains',
  starts_with: 'starts with',
  ends_with: 'ends with',
  not_starts_with: 'not starts with',
  not_ends_with: 'not ends with',
  greater_than: '>',
  less_than: '<',
  greater_than_or_equal: '>=',
  less_than_or_equal: '<='
};

/** kaneai ExecutionTimeBadge: ClockIcon 14 + "785ms"/"5.7s". */
function DurationChip(_ref) {
  var ms = _ref.ms;
  var label = formatChipDuration(ms);
  if (label === null) return null;
  return /*#__PURE__*/_jsxs("span", {
    className: styles.durationChip,
    "aria-label": "Execution time: ".concat(label),
    children: [/*#__PURE__*/_jsx(ClockIcon, {
      size: 14,
      fill: "#636C76",
      "aria-hidden": "true"
    }), label]
  });
}
var OperandPill = function OperandPill(_ref2) {
  var value = _ref2.value,
    title = _ref2.title;
  return /*#__PURE__*/_jsx("span", {
    className: styles.operandPill,
    title: title,
    children: value
  });
};

/** kaneai AssertionCommandV4: Assert {{var}} == expected · ✓/✗ result. */
function AssertionContent(_ref3) {
  var step = _ref3.step;
  var a = step.assertion;
  if (!a || !a.extracted && !a.expected) {
    return /*#__PURE__*/_jsx("div", {
      className: styles.assertBody,
      children: /*#__PURE__*/_jsxs("div", {
        className: styles.assertRow,
        children: [/*#__PURE__*/_jsx("span", {
          className: styles.assertKeyword,
          children: step.title
        }), a && a.passed !== null && /*#__PURE__*/_jsx(AssertResult, {
          passed: a.passed
        })]
      })
    });
  }
  var operator = a.operator && OPERATOR_DISPLAY[a.operator] || '==';
  return /*#__PURE__*/_jsx("div", {
    className: styles.assertBody,
    children: /*#__PURE__*/_jsxs("div", {
      className: styles.assertRow,
      children: [/*#__PURE__*/_jsx("span", {
        className: styles.assertKeyword,
        children: "Assert"
      }), a.extracted && /*#__PURE__*/_jsx(OperandPill, {
        value: a.extracted
      }), /*#__PURE__*/_jsx("span", {
        className: styles.assertOperator,
        children: operator
      }), a.expected && /*#__PURE__*/_jsx(OperandPill, {
        value: a.expected
      }), a.passed !== null && /*#__PURE__*/_jsx(AssertResult, {
        passed: a.passed
      })]
    })
  });
}
var NetworkCallPill = function NetworkCallPill(_ref4) {
  var _call$method, _call$url, _call$url2;
  var call = _ref4.call;
  return /*#__PURE__*/_jsxs("span", {
    className: styles.assertRow,
    children: [/*#__PURE__*/_jsx("span", {
      className: styles.scriptCmdPill,
      children: "DRIVER_QUERY"
    }), /*#__PURE__*/_jsx("span", {
      className: styles.dbOnText,
      children: "on"
    }), /*#__PURE__*/_jsx(OperandPill, {
      value: "".concat((_call$method = call.method) !== null && _call$method !== void 0 ? _call$method : '', " ").concat(truncateMiddle((_call$url = call.url) !== null && _call$url !== void 0 ? _call$url : '', 40)).trim(),
      title: (_call$url2 = call.url) !== null && _call$url2 !== void 0 ? _call$url2 : undefined
    })]
  });
};

/** kaneai NetworkAssertionInstructionV4 port: the DRIVER_QUERY row, then
 * "Verify if <left> <op> <right>" rows joined by the AND/OR linker and the
 * verdict. The failure condition is parsed but not shown (not supported yet). */
function NetworkContent(_ref5) {
  var _na$call;
  var step = _ref5.step;
  var query = step.networkQuery;
  var na = step.networkAssertion;
  if (!na && query) {
    return /*#__PURE__*/_jsxs("div", {
      className: styles.assertBody,
      children: [/*#__PURE__*/_jsx("span", {
        className: styles.assertKeyword,
        children: step.title
      }), /*#__PURE__*/_jsx(NetworkCallPill, {
        call: query
      })]
    });
  }
  if (!na) return null;
  var call = (_na$call = na.call) !== null && _na$call !== void 0 ? _na$call : query;
  return /*#__PURE__*/_jsxs("div", {
    className: styles.assertBody,
    children: [/*#__PURE__*/_jsxs("div", {
      className: styles.assertRow,
      children: [/*#__PURE__*/_jsx("span", {
        className: styles.assertKeyword,
        children: step.title
      }), na.passed !== null && /*#__PURE__*/_jsx(AssertResult, {
        passed: na.passed
      })]
    }), call && /*#__PURE__*/_jsx(NetworkCallPill, {
      call: call
    }), na.rows.map(function (row, i) {
      var _ref6, _row$leftValue, _row$operator, _ref7, _row$rightValue, _ref8, _row$rightValue2;
      return /*#__PURE__*/_jsxs(React.Fragment, {
        children: [i > 0 && /*#__PURE__*/_jsx("span", {
          className: styles.netLinker,
          children: na.linker
        }), /*#__PURE__*/_jsxs("span", {
          className: "".concat(styles.assertRow, " ").concat(styles.netRow),
          children: [/*#__PURE__*/_jsx("span", {
            className: styles.netVerify,
            children: "Verify if"
          }), /*#__PURE__*/_jsx(OperandPill, {
            value: [row.left, row.leftValue && truncateMiddle(row.leftValue, 20)].filter(Boolean).join(' '),
            title: (_ref6 = (_row$leftValue = row.leftValue) !== null && _row$leftValue !== void 0 ? _row$leftValue : row.left) !== null && _ref6 !== void 0 ? _ref6 : undefined
          }), /*#__PURE__*/_jsx("span", {
            className: styles.assertOperator,
            children: (_row$operator = row.operator) !== null && _row$operator !== void 0 ? _row$operator : 'equals'
          }), /*#__PURE__*/_jsx(OperandPill, {
            value: truncateMiddle((_ref7 = (_row$rightValue = row.rightValue) !== null && _row$rightValue !== void 0 ? _row$rightValue : row.right) !== null && _ref7 !== void 0 ? _ref7 : '', 20),
            title: (_ref8 = (_row$rightValue2 = row.rightValue) !== null && _row$rightValue2 !== void 0 ? _row$rightValue2 : row.right) !== null && _ref8 !== void 0 ? _ref8 : undefined
          })]
        })]
      }, i);
    })]
  });
}

/** kaneai ApiInstructionV4 status-pill colors: 2xx green, 3xx orange,
 * else red. */
function apiStatusColors(status) {
  var st = status === null ? '' : String(status);
  if (st.startsWith('2')) return {
    bg: '#DAFBE1',
    text: '#1F883D'
  };
  if (st.startsWith('3')) return {
    bg: '#FFF1E5',
    text: '#BC4C00'
  };
  return {
    bg: '#FFEBE9',
    text: '#D1242F'
  };
}

/**
 * kaneai ApiInstructionV4 port (v4/instructions/) — the API block body:
 * method pill · response-status pill · "in N ms" pill, then the mono URL
 * card. kaneai's "Show API" opens ApiFlowModal (live-app Redux); here it
 * expands the SEALED api_data inline as pretty-printed Request/Response.
 */
/** EXECUTE_CUSTOM_SCRIPT body — interpreter pill, a faded 2-line snippet
 * preview and a centered "Show script" pill (kaneai script-card idiom)
 * opening the terminal modal. */
function CustomScriptContent(_ref9) {
  var script = _ref9.script;
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    open = _React$useState2[0],
    setOpen = _React$useState2[1];
  return /*#__PURE__*/_jsxs("span", {
    className: styles.scriptBody,
    children: [/*#__PURE__*/_jsxs("span", {
      className: styles.scriptPreviewWrap,
      children: [/*#__PURE__*/_jsx("pre", {
        className: styles.scriptPreview,
        "aria-hidden": "true",
        children: script.scriptContent
      }), /*#__PURE__*/_jsx("button", {
        type: "button",
        className: styles.scriptShowBtn,
        onClick: function onClick(e) {
          e.stopPropagation();
          setOpen(true);
        },
        children: "Show script"
      })]
    }), /*#__PURE__*/_jsxs("span", {
      className: styles.scriptMetaRow,
      children: [script.command !== '' && /*#__PURE__*/_jsx("span", {
        className: styles.scriptCmdPill,
        children: script.command
      }), script.execId && /*#__PURE__*/_jsx("span", {
        className: styles.scriptExecId,
        title: script.execId,
        children: script.execId
      })]
    }), open && /*#__PURE__*/_jsx(CustomScriptModal, {
      script: script,
      onClose: function onClose() {
        return setOpen(false);
      }
    })]
  });
}

/** content_type 'script' body — kaneai ScriptInstructionV4 port: faded
 * snippet + Show code pill, "script" badge, result/output-variable line
 * (TE-27602). Show Code and the variable row open ONE modal (JS Snippet |
 * Variables tabs), exactly as kaneai does. */
function JsScriptContent(_ref0) {
  var step = _ref0.step;
  var _React$useState3 = React.useState(null),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    modalTab = _React$useState4[0],
    setModalTab = _React$useState4[1];
  var js = step.jsScript;
  if (!js) return null;
  return /*#__PURE__*/_jsxs("span", {
    className: styles.scriptBody,
    children: [/*#__PURE__*/_jsxs("span", {
      className: styles.richBadgeRow,
      children: [/*#__PURE__*/_jsx(AssertionCheckIcon, {}), /*#__PURE__*/_jsx("span", {
        className: styles.scriptCmdPill,
        children: "script"
      })]
    }), /*#__PURE__*/_jsxs("span", {
      className: styles.scriptPreviewWrap,
      children: [/*#__PURE__*/_jsx("pre", {
        className: styles.scriptPreview,
        "aria-hidden": "true",
        children: js.snippet
      }), /*#__PURE__*/_jsx("button", {
        type: "button",
        className: styles.scriptShowBtn,
        onClick: function onClick(e) {
          e.stopPropagation();
          setModalTab('code');
        },
        children: "Show Code"
      })]
    }), js.outputVariable && js.resultValue !== null && /*#__PURE__*/_jsx(SetVariableRow, {
      name: js.outputVariable,
      value: js.resultValue,
      onOpen: function onOpen() {
        return setModalTab('variables');
      }
    }), js.errorMessage && /*#__PURE__*/_jsxs("span", {
      className: styles.richErrorLine,
      children: [js.errorLine !== null ? "Error at line ".concat(js.errorLine, ": ") : '', js.errorMessage]
    }), modalTab && /*#__PURE__*/_jsx(SnippetVariablesModal, {
      title: "Execute JavaScript snippet",
      code: {
        command: 'javascript',
        scriptContent: js.snippet,
        timeoutSec: null,
        execId: js.outputVariable
      },
      error: js.errorMessage ? {
        line: js.errorLine,
        message: js.errorMessage
      } : null,
      variables: js.variables,
      initialTab: modalTab,
      onClose: function onClose() {
        return setModalTab(null);
      }
    })]
  });
}

/** Enter/Space on the card itself selects the step. Keys aimed at a nested
 * control (Show Code, the variable row) are left alone — preventDefault here
 * would cancel that button's own activation. */
var cardKeyDown = function cardKeyDown(select) {
  return function (e) {
    if (e.target !== e.currentTarget) {
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      select();
    }
  };
};

/** kaneai VariableViewerFooter port — "set <name> as <value> JSON" pill
 * row under JS/DB cards (TE-27602). Middle-truncated with full values in
 * tooltips, exactly the summary-page idiom; the whole row opens the
 * variables modal. */
function SetVariableRow(_ref1) {
  var name = _ref1.name,
    value = _ref1.value,
    onOpen = _ref1.onOpen;
  return /*#__PURE__*/_jsxs("button", {
    type: "button",
    className: "".concat(styles.richBadgeRow, " ").concat(styles.setVariableBtn),
    "aria-label": "View variable ".concat(name),
    "data-testid": "evidence-variable-open",
    onClick: function onClick(e) {
      e.stopPropagation();
      onOpen();
    },
    children: [/*#__PURE__*/_jsx(AssertionCheckIcon, {}), /*#__PURE__*/_jsx("span", {
      className: styles.setLabel,
      children: "set"
    }), /*#__PURE__*/_jsx("span", {
      className: styles.setPill,
      title: name,
      children: truncateMiddle(name, 10)
    }), /*#__PURE__*/_jsx("span", {
      className: styles.setLabel,
      children: "as"
    }), /*#__PURE__*/_jsx("span", {
      className: styles.setPill,
      title: value.slice(0, 500),
      children: truncateMiddle(value, 15)
    }), /*#__PURE__*/_jsx("span", {
      className: styles.setJsonBadge,
      children: "JSON"
    })]
  });
}

/** content_type 'db_query' body — kaneai DatabaseInstructionV4 port:
 * check + "query" badge + "on <db>", faded SQL + Show Code pill, and the
 * set-variable footer (TE-27602). */
function DbQueryContent(_ref10) {
  var step = _ref10.step;
  var _React$useState5 = React.useState(false),
    _React$useState6 = _slicedToArray(_React$useState5, 2),
    open = _React$useState6[0],
    setOpen = _React$useState6[1];
  var _React$useState7 = React.useState(false),
    _React$useState8 = _slicedToArray(_React$useState7, 2),
    variablesOpen = _React$useState8[0],
    setVariablesOpen = _React$useState8[1];
  var db = step.dbQuery;
  if (!db) return null;
  return /*#__PURE__*/_jsxs("span", {
    className: styles.scriptBody,
    children: [/*#__PURE__*/_jsxs("span", {
      className: styles.richBadgeRow,
      children: [/*#__PURE__*/_jsx(AssertionCheckIcon, {}), /*#__PURE__*/_jsx("span", {
        className: styles.scriptCmdPill,
        children: "query"
      }), db.dbName && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("span", {
          className: styles.dbOnText,
          children: "on"
        }), /*#__PURE__*/_jsx("span", {
          className: styles.dbNameText,
          children: db.dbName
        })]
      })]
    }), db.query && /*#__PURE__*/_jsxs("span", {
      className: styles.scriptPreviewWrap,
      children: [/*#__PURE__*/_jsx("pre", {
        className: styles.scriptPreview,
        "aria-hidden": "true",
        children: db.query
      }), /*#__PURE__*/_jsx("button", {
        type: "button",
        className: styles.scriptShowBtn,
        onClick: function onClick(e) {
          e.stopPropagation();
          setOpen(true);
        },
        children: "Show Code"
      })]
    }), db.outputVariable && db.output !== null ? /*#__PURE__*/_jsx(SetVariableRow, {
      name: db.outputVariable,
      value: db.output,
      onOpen: function onOpen() {
        return setVariablesOpen(true);
      }
    }) : db.output !== null && /*#__PURE__*/_jsxs("span", {
      className: styles.richResultLine,
      title: db.output,
      children: ["\u2192 ", db.output]
    }), open && db.query && /*#__PURE__*/_jsx(CustomScriptModal, {
      script: {
        command: 'sql',
        scriptContent: db.query,
        timeoutSec: null,
        execId: db.dbName
      },
      onClose: function onClose() {
        return setOpen(false);
      }
    }), variablesOpen && /*#__PURE__*/_jsx(SnippetVariablesModal, {
      title: "Database Query",
      code: null,
      variables: db.variables,
      initialTab: "variables",
      onClose: function onClose() {
        return setVariablesOpen(false);
      }
    })]
  });
}

/** content_type 'screenshot_command' footer — kaneai
 * ScreenshotInstructionV4 port: muted full-width strip with the SmartUI
 * verdict + bulb-and-strategy, kaneai's exact priority order (TE-27602). */
function VisualContent(_ref11) {
  var _v$resultStatus;
  var step = _ref11.step,
    status = _ref11.status;
  var v = step.visual;
  if (!v) return null;
  var hasShot = v.resultStatus !== null || v.mismatchPct !== null;
  var s = ((_v$resultStatus = v.resultStatus) !== null && _v$resultStatus !== void 0 ? _v$resultStatus : '').toLowerCase();
  var isNew = ['new-screenshot', 'newscreenshot'].includes(s);
  var isApproved = ['approved', 'newscreenshot', 'added to baseline', 'new-screenshot', 'moved'].includes(s);
  var footer = status === 'failed' ? {
    text: 'Error in taking screenshot',
    color: '#cf222e'
  } : v.baseline ? {
    text: 'Baseline set successfully',
    color: '#1a7f37'
  } : isNew ? {
    text: 'New Screenshot',
    color: '#0969da'
  } : isApproved ? {
    text: 'Approved',
    color: '#1a7f37'
  } : hasShot ? {
    text: 'Changes Found',
    color: '#cf222e'
  } : status === 'passed' ? {
    text: 'Screenshot captured',
    color: '#1a7f37'
  } : null;
  var strategyLabel = v.strategy ? v.strategy === 'smart-ignore' ? 'Smart Ignore' : 'Strict' : v.smartIgnore ? 'Smart Ignore' : null;
  if (!footer && !strategyLabel) return null;
  return /*#__PURE__*/_jsxs("span", {
    className: styles.visualFooter,
    children: [footer && /*#__PURE__*/_jsx("span", {
      style: {
        color: footer.color
      },
      children: footer.text
    }), footer && strategyLabel && /*#__PURE__*/_jsx("span", {
      className: styles.visualFooterDot
    }), strategyLabel && /*#__PURE__*/_jsxs("span", {
      className: styles.visualStrategy,
      children: [/*#__PURE__*/_jsx(LightBulbIcon, {
        size: 16,
        "aria-hidden": "true"
      }), strategyLabel]
    })]
  });
}
function ApiContent(_ref12) {
  var _api$responseStatus, _JSON$stringify;
  var step = _ref12.step,
    status = _ref12.status;
  var _React$useState9 = React.useState(false),
    _React$useState0 = _slicedToArray(_React$useState9, 2),
    modalOpen = _React$useState0[0],
    setModalOpen = _React$useState0[1];
  var _React$useState1 = React.useState(false),
    _React$useState10 = _slicedToArray(_React$useState1, 2),
    variablesOpen = _React$useState10[0],
    setVariablesOpen = _React$useState10[1];
  var api = step.api;
  if (!api) return null;
  // kaneai ApiInstructionV4: the first user variable drives the footer row.
  var firstVar = api.variables[0];
  var is4xx = String((_api$responseStatus = api.responseStatus) !== null && _api$responseStatus !== void 0 ? _api$responseStatus : '').startsWith('4');
  var colors = apiStatusColors(api.responseStatus);
  return /*#__PURE__*/_jsxs("span", {
    className: styles.apiBody,
    children: [/*#__PURE__*/_jsxs("span", {
      className: styles.apiPillRow,
      children: [/*#__PURE__*/_jsxs("span", {
        className: styles.apiPillLeft,
        children: [status === 'passed' && !is4xx && /*#__PURE__*/_jsx(AssertionCheckIcon, {}), status === 'passed' && is4xx && /*#__PURE__*/_jsx(AssertionXIcon, {}), /*#__PURE__*/_jsx("span", {
          className: styles.apiMethodPill,
          children: api.method
        })]
      }), api.responseStatus !== null && /*#__PURE__*/_jsxs("span", {
        className: styles.apiPillLeft,
        children: [/*#__PURE__*/_jsx("span", {
          className: styles.apiStatusPill,
          style: {
            background: colors.bg,
            color: colors.text
          },
          children: api.responseStatus
        }), api.responseTimeMs !== null && /*#__PURE__*/_jsxs("span", {
          className: styles.apiTimePill,
          children: ["in ", api.responseTimeMs.toFixed(2), " ms"]
        })]
      })]
    }), api.url && /*#__PURE__*/_jsx("span", {
      className: styles.apiUrlRail,
      children: /*#__PURE__*/_jsxs("span", {
        className: styles.apiUrlCard,
        children: [/*#__PURE__*/_jsx("span", {
          className: styles.apiUrl,
          children: api.url
        }), /*#__PURE__*/_jsx("button", {
          type: "button",
          className: styles.apiShowBtn,
          onClick: function onClick(e) {
            e.stopPropagation();
            setModalOpen(true);
          },
          children: "Show API"
        })]
      })
    }), firstVar && /*#__PURE__*/_jsx(SetVariableRow, {
      name: firstVar.name,
      value: (_JSON$stringify = JSON.stringify(firstVar.value)) !== null && _JSON$stringify !== void 0 ? _JSON$stringify : 'null',
      onOpen: function onOpen() {
        return setVariablesOpen(true);
      }
    }), modalOpen && /*#__PURE__*/_jsx(ApiModal, {
      api: api,
      onClose: function onClose() {
        return setModalOpen(false);
      }
    }), variablesOpen && /*#__PURE__*/_jsx(SnippetVariablesModal, {
      title: "API Variables",
      code: null,
      variables: api.variables,
      initialTab: "variables",
      onClose: function onClose() {
        return setVariablesOpen(false);
      }
    })]
  });
}
var AssertResult = function AssertResult(_ref13) {
  var passed = _ref13.passed;
  return /*#__PURE__*/_jsxs("span", {
    className: styles.assertResult,
    children: [passed ? /*#__PURE__*/_jsx(AssertionCheckIcon, {}) : /*#__PURE__*/_jsx(AssertionXIcon, {}), /*#__PURE__*/_jsx("span", {
      className: styles.assertResultText,
      style: {
        color: passed ? '#1A7F37' : '#CF222E'
      },
      children: passed ? 'Assertion True' : 'Assertion False'
    })]
  });
};

/**
 * kaneai InstructionCardV4 port: white 6px-radius card, 1px
 * rgba(208,215,222,0.7) border (selected 2px #0969DA, failed 1px #CF222E),
 * 38px content row with the command icon pinned left and the duration chip
 * top-right. Failed cards get the error-message row beneath (card-level UI,
 * not the excluded RCA banner).
 */
export function LeafCard(_ref14) {
  var _step$command, _step$ordinal4, _step$reasoning, _step$command2;
  var step = _ref14.step,
    status = _ref14.status,
    selected = _ref14.selected,
    chipHidden = _ref14.chipHidden,
    onSelect = _ref14.onSelect;
  var isAssertion = ((_step$command = step.command) !== null && _step$command !== void 0 ? _step$command : '').toLowerCase() === 'assertion';
  var isNetwork = step.networkQuery !== null || step.networkAssertion !== null;
  var isApi = step.api !== null;
  var isCustomScript = step.customScript !== null;
  var border = selected ? '2px solid #0969DA' : status === 'failed' ? '1px solid #CF222E' : '1px solid rgba(208, 215, 222, 0.7)';
  if (isCustomScript && step.customScript) {
    var _step$ordinal;
    /* Same div-wrapper constraint as the API block: the card hosts a real
       Show script button that can't nest inside a <button>. */
    // Fenced-title producer shape: the whole script can ride in the title
    // ("@executor python3 `…`") — show only the head, the body renders in
    // the snippet/modal. Falls back to the raw title when nothing strips.
    var scriptTitle = step.title.replace(/`[\s\S]*`\s*$/, '').trim() || step.title;
    return /*#__PURE__*/_jsxs("div", {
      className: styles.leafColumn,
      children: [/*#__PURE__*/_jsx("div", {
        role: "button",
        className: styles.leafCard,
        style: {
          border: border
        },
        "data-step-key": step.key,
        "aria-current": selected ? 'true' : undefined,
        "aria-label": "Step ".concat((_step$ordinal = step.ordinal) !== null && _step$ordinal !== void 0 ? _step$ordinal : '', ": ").concat(scriptTitle, ", ").concat(status === 'unknown' ? 'status unknown' : status),
        tabIndex: selected ? 0 : -1,
        onClick: function onClick() {
          return onSelect(step.key);
        },
        onKeyDown: cardKeyDown(function () {
          return onSelect(step.key);
        }),
        children: /*#__PURE__*/_jsxs("span", {
          className: styles.apiColumn,
          children: [/*#__PURE__*/_jsxs("span", {
            className: styles.apiHead,
            children: [/*#__PURE__*/_jsx("span", {
              className: styles.leafIcon,
              children: /*#__PURE__*/_jsx(CommandIcon, {
                command: "execute_custom_script",
                status: status
              })
            }), /*#__PURE__*/_jsx("span", {
              className: "".concat(styles.leafText, " ").concat(styles.apiTitle),
              children: scriptTitle
            }), !chipHidden && /*#__PURE__*/_jsx("span", {
              className: styles.leafChips,
              children: /*#__PURE__*/_jsx(DurationChip, {
                ms: step.durationMs
              })
            })]
          }), /*#__PURE__*/_jsx(CustomScriptContent, {
            script: step.customScript
          })]
        })
      }), status === 'failed' && step.errorMessage && /*#__PURE__*/_jsxs("div", {
        className: styles.leafError,
        children: [/*#__PURE__*/_jsx(QuestionIcon, {
          size: 16,
          fill: "#BF8700",
          "aria-hidden": "true"
        }), /*#__PURE__*/_jsx("span", {
          className: styles.leafErrorText,
          children: step.errorMessage
        })]
      })]
    });
  }
  /* TE-27602 rich cards — same div-wrapper constraint as API/custom-script
     (they host real Show code/query buttons). Visual has no button but
     keeps the shape for consistency. */
  if (step.jsScript || step.dbQuery || step.visual) {
    var _step$visual$name, _step$visual, _step$ordinal2;
    // Explicit glyph key — step.command can be null/overridden by the
    // result-side kind for these types, which fell back to the sparkle.
    var richIconCmd = step.jsScript ? 'script' : step.dbQuery ? 'db_query' : 'smartui_screenshot';
    var visualName = (_step$visual$name = (_step$visual = step.visual) === null || _step$visual === void 0 ? void 0 : _step$visual.name) !== null && _step$visual$name !== void 0 ? _step$visual$name : null;
    var richTitle = step.visual ? "Visual Comparison".concat(visualName ? " at \"".concat(visualName, "\"") : '') : step.title;
    return /*#__PURE__*/_jsxs("div", {
      className: styles.leafColumn,
      children: [/*#__PURE__*/_jsx("div", {
        role: "button",
        className: styles.leafCard,
        style: {
          border: border
        },
        "data-step-key": step.key,
        "aria-current": selected ? 'true' : undefined,
        "aria-label": "Step ".concat((_step$ordinal2 = step.ordinal) !== null && _step$ordinal2 !== void 0 ? _step$ordinal2 : '', ": ").concat(richTitle, ", ").concat(status === 'unknown' ? 'status unknown' : status),
        tabIndex: selected ? 0 : -1,
        onClick: function onClick() {
          return onSelect(step.key);
        },
        onKeyDown: cardKeyDown(function () {
          return onSelect(step.key);
        }),
        children: /*#__PURE__*/_jsxs("span", {
          className: styles.apiColumn,
          children: [/*#__PURE__*/_jsxs("span", {
            className: styles.apiHead,
            children: [/*#__PURE__*/_jsx("span", {
              className: styles.leafIcon,
              children: /*#__PURE__*/_jsx(CommandIcon, {
                command: richIconCmd,
                status: status
              })
            }), /*#__PURE__*/_jsx("span", {
              className: "".concat(styles.leafText, " ").concat(styles.apiTitle),
              children: step.visual && visualName ? /*#__PURE__*/_jsxs(_Fragment, {
                children: ['Visual Comparison at "', /*#__PURE__*/_jsx("strong", {
                  children: visualName
                }), '"']
              }) : richTitle
            }), !chipHidden && /*#__PURE__*/_jsx("span", {
              className: styles.leafChips,
              children: /*#__PURE__*/_jsx(DurationChip, {
                ms: step.durationMs
              })
            })]
          }), step.jsScript && /*#__PURE__*/_jsx(JsScriptContent, {
            step: step
          }), step.dbQuery && /*#__PURE__*/_jsx(DbQueryContent, {
            step: step
          }), step.visual && /*#__PURE__*/_jsx(VisualContent, {
            step: step,
            status: status
          })]
        })
      }), status === 'failed' && step.errorMessage && /*#__PURE__*/_jsxs("div", {
        className: styles.leafError,
        children: [/*#__PURE__*/_jsx(QuestionIcon, {
          size: 16,
          fill: "#BF8700",
          "aria-hidden": "true"
        }), /*#__PURE__*/_jsx("span", {
          className: styles.leafErrorText,
          children: step.errorMessage
        })]
      })]
    });
  }
  if (isApi) {
    var _step$ordinal3;
    /* div wrapper: the API block hosts a real Show API button, which
       can't nest inside a <button>. Same classes/aria as the button. */
    return /*#__PURE__*/_jsxs("div", {
      className: styles.leafColumn,
      children: [/*#__PURE__*/_jsx("div", {
        role: "button",
        className: styles.leafCard,
        style: {
          border: border
        },
        "data-step-key": step.key,
        "aria-current": selected ? 'true' : undefined,
        "aria-label": "Step ".concat((_step$ordinal3 = step.ordinal) !== null && _step$ordinal3 !== void 0 ? _step$ordinal3 : '', ": ").concat(step.title, ", ").concat(status === 'unknown' ? 'status unknown' : status),
        tabIndex: selected ? 0 : -1,
        onClick: function onClick() {
          return onSelect(step.key);
        },
        onKeyDown: cardKeyDown(function () {
          return onSelect(step.key);
        }),
        children: /*#__PURE__*/_jsxs("span", {
          className: styles.apiColumn,
          children: [/*#__PURE__*/_jsxs("span", {
            className: styles.apiHead,
            children: [/*#__PURE__*/_jsx("span", {
              className: styles.leafIcon,
              children: /*#__PURE__*/_jsx(CommandIcon, {
                command: "api",
                status: status
              })
            }), /*#__PURE__*/_jsx("span", {
              className: "".concat(styles.leafText, " ").concat(styles.apiTitle),
              children: step.title
            }), !chipHidden && /*#__PURE__*/_jsx("span", {
              className: styles.leafChips,
              children: /*#__PURE__*/_jsx(DurationChip, {
                ms: step.durationMs
              })
            })]
          }), /*#__PURE__*/_jsx(ApiContent, {
            step: step,
            status: status
          })]
        })
      }), status === 'failed' && step.errorMessage && /*#__PURE__*/_jsxs("div", {
        className: styles.leafError,
        children: [/*#__PURE__*/_jsx(QuestionIcon, {
          size: 16,
          fill: "#BF8700",
          "aria-hidden": "true"
        }), /*#__PURE__*/_jsx("span", {
          className: styles.leafErrorText,
          children: step.errorMessage
        })]
      })]
    });
  }
  return /*#__PURE__*/_jsxs("div", {
    className: styles.leafColumn,
    children: [/*#__PURE__*/_jsxs("button", {
      type: "button",
      className: styles.leafCard,
      style: {
        border: border
      },
      "data-step-key": step.key,
      "aria-current": selected ? 'true' : undefined,
      "aria-label": "Step ".concat((_step$ordinal4 = step.ordinal) !== null && _step$ordinal4 !== void 0 ? _step$ordinal4 : '', ": ").concat(step.title, ", ").concat(status === 'unknown' ? 'status unknown' : status),
      tabIndex: selected ? 0 : -1,
      onClick: function onClick() {
        return onSelect(step.key);
      },
      title: (_step$reasoning = step.reasoning) !== null && _step$reasoning !== void 0 ? _step$reasoning : undefined,
      children: [/*#__PURE__*/_jsx("span", {
        className: styles.leafIcon,
        children: /*#__PURE__*/_jsx(CommandIcon, {
          command: (_step$command2 = step.command) !== null && _step$command2 !== void 0 ? _step$command2 : step.kind,
          status: status
        })
      }), /*#__PURE__*/_jsxs("span", {
        className: "".concat(styles.leafText, " ").concat(isAssertion || isNetwork ? styles.leafTextAssert : ''),
        children: [isNetwork && /*#__PURE__*/_jsx(NetworkContent, {
          step: step
        }), !isNetwork && (isAssertion ? /*#__PURE__*/_jsx(AssertionContent, {
          step: step
        }) : step.title)]
      }), !chipHidden && /*#__PURE__*/_jsx("span", {
        className: styles.leafChips,
        children: /*#__PURE__*/_jsx(DurationChip, {
          ms: step.durationMs
        })
      })]
    }), status === 'failed' && step.errorMessage && /*#__PURE__*/_jsxs("div", {
      className: styles.leafError,
      children: [/*#__PURE__*/_jsx(QuestionIcon, {
        size: 16,
        fill: "#BF8700",
        "aria-hidden": "true"
      }), /*#__PURE__*/_jsx("span", {
        className: styles.leafErrorText,
        children: step.errorMessage
      })]
    })]
  });
}